/**
 * BrandManager Component
 * Manages brand-specific UI elements (favicon, title) throughout the application
 * Requirements: 4_4, 4_5
 */

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useBrand } from '../hooks/useBrand';
import { MEMORY_STORE, QUERY_STRING_STORE } from '../helpers/constants/storage';

function BrandManager() {
  const { updateBrandFromTenant } = useBrand();
  const lastBrandRef = useRef(null);

  // Get user data to check if logged in
  const user = useSelector((state) => state.memory?.user || null);

  // Get all tenants from Redux store
  const tenants = useSelector((state) =>
    state.memory?.[MEMORY_STORE.TENANTS] ||
    state.session?.[MEMORY_STORE.TENANTS] ||
    []
  );

  // Get currently selected tenant ID
  const selectedTenantId = useSelector((state) =>
    state.session?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0] ||
    null
  );

  /**
   * Effect to update brand when user logs in and tenant data is available
   * Optimized to only run when brand actually changes
   */
  useEffect(() => {
    if (user && tenants && tenants.length > 0) {
      // Find the current tenant based on selected tenant ID or use first tenant
      let currentTenant = null;

      if (selectedTenantId) {
        currentTenant = tenants.find(t => t.tenant_id === selectedTenantId);
      }

      if (!currentTenant && tenants.length > 0) {
        currentTenant = tenants[0];
      }

      // Update brand if tenant has brand field AND it's different from last time
      if (currentTenant?.brand && currentTenant.brand !== lastBrandRef.current) {
        lastBrandRef.current = currentTenant.brand;
        updateBrandFromTenant(currentTenant);
      }
    }
  }, [user, tenants, selectedTenantId, updateBrandFromTenant]);

  // This component doesn't render anything
  return null;
}

export default BrandManager;
